import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { Toaster } from '@/components/ui/sonner';
import SessionTimeoutDialog from '@/components/SessionTimeoutDialog';
import ErrorBoundary from '@/components/ErrorBoundary';

// Public Pages
import HomePage from '@/pages/HomePage';
import ReportFormPage from '@/pages/ReportFormPage';
import TrackReportPage from '@/pages/TrackReportPage';
import LoginPage from '@/pages/LoginPage';
import ForgotPasswordPage from '@/pages/ForgotPasswordPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';
import MFASetupPage from '@/pages/MFASetupPage';
import ForceChangePasswordPage from '@/pages/ForceChangePasswordPage';
import ChooseTenantPage from '@/pages/ChooseTenantPage';
import CognisegReportsPage from '@/pages/CognisegReportsPage';
import PolicyPage from '@/pages/PolicyPage';
import VerifySignaturePage from '@/pages/VerifySignaturePage';

// Admin Pages
import AdminLayout from '@/pages/admin/AdminLayout';
import DashboardPage from '@/pages/admin/DashboardPage';
import ReportsListPage from '@/pages/admin/ReportsListPage';
import ReportDetailPage from '@/pages/admin/ReportDetailPage';
import SettingsPage from '@/pages/admin/SettingsPage';
import UsersPage from '@/pages/admin/UsersPage';
import AnalyticsPage from '@/pages/admin/AnalyticsPage';
import EmailTemplatesPage from '@/pages/admin/EmailTemplatesPage';
import WorkflowConfigPage from '@/pages/admin/WorkflowConfigPage';
import ConflictsPage from '@/pages/admin/ConflictsPage';
import RetentionConfigPage from '@/pages/admin/RetentionConfigPage';
import ScheduledReportsPage from '@/pages/admin/ScheduledReportsPage';
import BillingPage from '@/pages/admin/BillingPage';

// Super Admin Pages
import SuperAdminLayout from '@/pages/superadmin/SuperAdminLayout';
import SuperAdminDashboard from '@/pages/superadmin/SuperAdminDashboard';
import TenantsManagementPage from '@/pages/superadmin/TenantsManagementPage';
import PlatformSettingsPage from '@/pages/superadmin/PlatformSettingsPage';
import BackupManagementPage from '@/pages/superadmin/BackupManagementPage';
import AuditLogsPage from '@/pages/superadmin/AuditLogsPage';
import ProvisioningPage from '@/pages/superadmin/ProvisioningPage';
import EmailLogsPage from '@/pages/superadmin/EmailLogsPage';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, user, tenant } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-pulse text-slate-400">A carregar...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (user?.must_change_password) {
    return <Navigate to="/alterar-password" replace />;
  }

  // CogniSeg global triage user must pick a tenant before entering any /admin page.
  // The `tenant` field is only populated after a successful /cogniseg/select-tenant.
  if (user?.is_cogniseg_global && !tenant) {
    return <Navigate to="/cogniseg" replace />;
  }

  return children;
};

// Auth-only guard (no forced-password redirect) — used by the change-password page
const RequireAuth = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-pulse text-slate-400">A carregar...</div>
      </div>
    );
  }
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  return children;
};

// Super Admin Protected Route
const SuperAdminRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="animate-pulse text-amber-500">A carregar...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (user?.must_change_password) {
    return <Navigate to="/alterar-password" replace />;
  }

  if (user?.role !== 'super_admin' || user?.is_cogniseg_global) {
    return <Navigate to="/admin" replace />;
  }

  return children;
};

// App Content with Routes
const AppContent = () => {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<HomePage />} />
      <Route path="/denunciar/:tenantSlug" element={<ReportFormPage />} />
      <Route path="/seguimento" element={<TrackReportPage />} />
      <Route path="/verificar/:reportId" element={<VerifySignaturePage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/recuperar-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />

      {/* Public tenant policy pages (footer links) */}
      <Route path="/privacidade/:tenantSlug" element={<PolicyPage kind="privacy" />} />
      <Route path="/termos/:tenantSlug" element={<PolicyPage kind="terms" />} />

      {/* Forced password change (after admin reset) - authenticated only */}
      <Route
        path="/alterar-password"
        element={
          <RequireAuth>
            <ForceChangePasswordPage />
          </RequireAuth>
        }
      />

      {/* CogniSeg global user — tenant picker */}
      <Route
        path="/cogniseg"
        element={
          <RequireAuth>
            <ChooseTenantPage />
          </RequireAuth>
        }
      />
      <Route
        path="/cogniseg/denuncias"
        element={
          <RequireAuth>
            <CognisegReportsPage />
          </RequireAuth>
        }
      />

      {/* MFA Setup - Protected */}
      <Route
        path="/mfa-setup"
        element={
          <ProtectedRoute>
            <MFASetupPage />
          </ProtectedRoute>
        }
      />

      {/* Protected Admin Routes */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <AdminLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<DashboardPage />} />
        <Route path="denuncias" element={<ReportsListPage />} />
        <Route path="denuncias/:reportId" element={<ReportDetailPage />} />
        <Route path="utilizadores" element={<UsersPage />} />
        <Route path="faturacao" element={<BillingPage />} />
        <Route path="definicoes" element={<SettingsPage />} />
        <Route path="relatorios" element={<AnalyticsPage />} />
        <Route path="templates-email" element={<EmailTemplatesPage />} />
        <Route path="workflow" element={<WorkflowConfigPage />} />
        <Route path="conflitos" element={<ConflictsPage />} />
        <Route path="retencao" element={<RetentionConfigPage />} />
        <Route path="relatorios-agendados" element={<ScheduledReportsPage />} />
      </Route>

      {/* Super Admin Routes */}
      <Route
        path="/super-admin"
        element={
          <SuperAdminRoute>
            <SuperAdminLayout />
          </SuperAdminRoute>
        }
      >
        <Route index element={<SuperAdminDashboard />} />
        <Route path="tenants" element={<TenantsManagementPage />} />
        <Route path="configuracoes" element={<PlatformSettingsPage />} />
        <Route path="backups" element={<BackupManagementPage />} />
        <Route path="auditoria" element={<AuditLogsPage />} />
        <Route path="emails" element={<EmailLogsPage />} />
        <Route path="provisionamento" element={<ProvisioningPage />} />
      </Route>

      {/* Public whistleblowing channel at root: etica.cogniseg.net/{slug} */}
      <Route path="/:tenantSlug" element={<ReportFormPage />} />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <AppContent />
          <SessionTimeoutDialog />
          <Toaster position="top-right" />
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
